import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../../../context/AuthContext";
import { instructorApi } from "../../../services/instructorApi";
import { resolvePublicDisplayName, generateDisplayNameOptions } from "../../../lib/displayName";
import OnboardingStepFrame from "../../../components/instructor/OnboardingStepFrame";

const inputClass =
  "mt-1.5 w-full rounded-lg border border-emerald-200 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:outline-none focus:border-emerald-600 focus:ring-2 focus:ring-emerald-600/20";

const labelClass = "text-sm font-medium text-gray-900";

const sectionClass =
  "rounded-2xl border border-emerald-100/90 bg-white/90 p-5 shadow-sm sm:p-6 space-y-4";

function normalizeName(value) {
  return String(value || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();
}

const HEADLINE_EXAMPLES = [
  "Certified Yoga Therapist & Meditation Instructor",
  "Hatha & Vinyasa Yoga Teacher",
  "Prenatal & Postnatal Yoga Specialist",
  "Meditation & Mindfulness Coach",
  "Online Yoga Teacher · Live Classes",
  "Ashtanga & Mysore Style Instructor",
  "Yin Yoga & Restorative Practice Guide",
  "Kids & Family Yoga Teacher",
  "Yoga for Stress Relief & Sleep",
  "Corporate Wellness & Chair Yoga Coach",
  "Breathwork & Pranayama Facilitator",
  "200-Hour RYT · Beginner Friendly Classes",
];

/** Common languages instructors select during basics onboarding. */
const LANGUAGE_OPTIONS = [
  "English",
  "Hindi",
  "Sanskrit",
  "Spanish",
  "French",
  "German",
  "Portuguese",
  "Italian",
  "Dutch",
  "Swedish",
  "Norwegian",
  "Danish",
  "Mandarin",
  "Japanese",
  "Korean",
  "Arabic",
  "Russian",
  "Tamil",
  "Telugu",
  "Kannada",
  "Malayalam",
  "Bengali",
  "Marathi",
  "Gujarati",
  "Punjabi",
];

const TIMEZONES = [
  { value: "IST", label: "IST — India Standard Time (UTC+5:30)" },
  { value: "NPT", label: "NPT — Nepal Time (UTC+5:45)" },
  { value: "PKT", label: "PKT — Pakistan Standard Time (UTC+5)" },
  { value: "BST", label: "BST — Bangladesh Standard Time (UTC+6)" },
  { value: "ICT", label: "ICT — Indochina Time (UTC+7)" },
  { value: "CST Asia", label: "CST — China Standard Time (UTC+8)" },
  { value: "SGT", label: "SGT — Singapore Time (UTC+8)" },
  { value: "JST", label: "JST — Japan Standard Time (UTC+9)" },
  { value: "AEST", label: "AEST — Australian Eastern (UTC+10)" },
  { value: "ACST", label: "ACST — Australian Central (UTC+9:30)" },
  { value: "AWST", label: "AWST — Australian Western (UTC+8)" },
  { value: "NZST", label: "NZST — New Zealand (UTC+12)" },
  { value: "GST", label: "GST — Gulf Standard Time (UTC+4)" },
  { value: "MSK", label: "MSK — Moscow Standard Time (UTC+3)" },
  { value: "EET", label: "EET — Eastern European (UTC+2)" },
  { value: "CET", label: "CET — Central European (UTC+1)" },
  { value: "WET", label: "WET — Western European (UTC+0)" },
  { value: "GMT", label: "GMT — Greenwich Mean Time (UTC+0)" },
  { value: "UTC", label: "UTC — Coordinated Universal Time" },
  { value: "BST UK", label: "BST — British Summer Time (UTC+1)" },
  { value: "AST", label: "AST — Atlantic Standard Time (UTC−4)" },
  { value: "EST", label: "EST — Eastern Time US/Canada (UTC−5)" },
  { value: "CST", label: "CST — Central Time US/Canada (UTC−6)" },
  { value: "MST", label: "MST — Mountain Time US/Canada (UTC−7)" },
  { value: "PST", label: "PST — Pacific Time US/Canada (UTC−8)" },
  { value: "AKST", label: "AKST — Alaska Time (UTC−9)" },
  { value: "HST", label: "HST — Hawaii Time (UTC−10)" },
  { value: "BRT", label: "BRT — Brasília Time (UTC−3)" },
  { value: "ART", label: "ART — Argentina Time (UTC−3)" },
  { value: "SAST", label: "SAST — South Africa Standard (UTC+2)" },
  { value: "EAT", label: "EAT — East Africa Time (UTC+3)" },
  { value: "WAT", label: "WAT — West Africa Time (UTC+1)" },
];

function SectionTitle({ children, hint }) {
  return (
    <div className="mb-1">
      <h2 className="text-sm font-semibold uppercase tracking-[0.12em] text-emerald-800/80">
        {children}
      </h2>
      {hint ? <p className="mt-1 text-xs text-gray-500">{hint}</p> : null}
    </div>
  );
}

export default function BasicsStep() {
  const { instructor, refreshInstructor } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    legal_name: "",
    name_style: "full_initials",
    display_name_custom: "",
    gender: "",
    headline: "",
    country: "",
    city: "",
    timezone: "IST",
    languages: [],
    otherLanguage: "",
    youtube: "",
    phone: "",
  });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!instructor?.profile) return;
    const profile = instructor.profile;
    const savedTz = profile.timezone || "IST";
    const known = TIMEZONES.some((tz) => tz.value === savedTz);
    let style = profile.name_style || "full_initials";
    if (style === "full_legal") style = "full_initials";
    if (!["full_initials", "privacy", "custom"].includes(style)) style = "full_initials";
    const savedLangs = Array.isArray(profile.languages) ? profile.languages : [];
    const presets = LANGUAGE_OPTIONS.filter((lang) =>
      savedLangs.some((s) => String(s).toLowerCase() === lang.toLowerCase())
    );
    const extras = savedLangs.filter(
      (s) => !LANGUAGE_OPTIONS.some((lang) => lang.toLowerCase() === String(s).toLowerCase())
    );
    setForm({
      legal_name: profile.legal_name || profile.display_name || "",
      name_style: style,
      display_name_custom: style === "custom" ? profile.display_name || "" : "",
      gender: profile.gender === "Male" || profile.gender === "Female" ? profile.gender : "",
      headline: profile.headline || "",
      country: profile.country || "",
      city: profile.city || "",
      timezone: known ? savedTz : "IST",
      languages: presets,
      otherLanguage: extras.join(", "),
      youtube: profile.youtube || "",
      phone: profile.phone || "",
    });
  }, [instructor]);

  const nameOptions = useMemo(
    () => generateDisplayNameOptions(form.legal_name),
    [form.legal_name]
  );

  const publicPreview = useMemo(
    () =>
      resolvePublicDisplayName({
        legalName: form.legal_name,
        nameStyle: form.name_style,
        customDisplayName: form.display_name_custom,
      }),
    [form.legal_name, form.name_style, form.display_name_custom]
  );

  const customMatchesLegal =
    form.name_style === "custom" &&
    normalizeName(form.display_name_custom) &&
    normalizeName(form.display_name_custom) === normalizeName(form.legal_name);

  const showPrivacyOption =
    nameOptions.privacy && nameOptions.privacy !== nameOptions.full_initials;

  const locationLabel = [form.city, form.country].filter(Boolean).join(", ");

  function update(key, value) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function toggleLanguage(lang) {
    setForm((f) => {
      const has = f.languages.includes(lang);
      return {
        ...f,
        languages: has ? f.languages.filter((l) => l !== lang) : [...f.languages, lang],
      };
    });
  }

  function collectLanguages() {
    const extra = (form.otherLanguage || "")
      .split(/[|,]/)
      .map((s) => s.trim())
      .filter(Boolean);
    const merged = [...form.languages];
    for (const item of extra) {
      if (!merged.some((l) => l.toLowerCase() === item.toLowerCase())) {
        merged.push(item);
      }
    }
    return merged;
  }

  async function handleSave(e) {
    e.preventDefault();
    setError("");
    if (customMatchesLegal) {
      setError(
        "Custom public name cannot match your full legal name. Choose an abbreviated option or a different custom name."
      );
      return;
    }
    const languages = collectLanguages();
    if (languages.length < 1) {
      setError("Select at least one language you teach in.");
      return;
    }
    setSaving(true);
    try {
      const profile = instructor?.profile;
      await instructorApi.saveStep("basics", {
        legal_name: form.legal_name,
        name_style: form.name_style,
        display_name_custom: form.name_style === "custom" ? form.display_name_custom : undefined,
        display_name: publicPreview,
        gender: form.gender,
        headline: form.headline,
        country: form.country,
        city: form.city,
        timezone: form.timezone,
        website: profile?.website || null,
        instagram: profile?.instagram || null,
        youtube: form.youtube,
        phone: form.phone,
        languages,
      });
      await refreshInstructor();
      navigate("/instructor/onboarding/expertise");
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <OnboardingStepFrame
      title="Start with your teaching identity"
      subtitle="Tell us who you are. Students will see your public name and headline — legal details stay private."
      wide
    >
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_240px] lg:items-start">
        <form onSubmit={handleSave} className="space-y-5">
          {error && (
            <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              {error}
            </div>
          )}

          <motion.section
            className={sectionClass}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
          >
            <SectionTitle hint="Used for verification only — never shown publicly.">
              Identity
            </SectionTitle>
            <label className="block">
              <span className={labelClass}>Legal name (private)</span>
              <input
                className={inputClass}
                required
                value={form.legal_name}
                onChange={(e) => update("legal_name", e.target.value)}
                placeholder="e.g. Vikrant Prabhu Desai"
                autoComplete="name"
              />
            </label>

            <fieldset>
              <legend className={labelClass}>Gender</legend>
              <div className="mt-2 flex flex-wrap gap-4">
                {["Male", "Female"].map((g) => (
                  <label key={g} className="flex items-center gap-2 text-sm text-gray-800">
                    <input
                      type="radio"
                      className="accent-emerald-600"
                      name="gender"
                      required={g === "Male"}
                      checked={form.gender === g}
                      onChange={() => update("gender", g)}
                    />
                    <span>{g}</span>
                  </label>
                ))}
              </div>
            </fieldset>

            <fieldset className="space-y-2">
              <legend className={labelClass}>Public profile name</legend>
              <p className="text-xs text-gray-500">
                How students see you. Full legal name is not shown.
              </p>
              <label className="flex items-start gap-2 text-sm text-gray-800">
                <input
                  type="radio"
                  className="mt-0.5 accent-emerald-600"
                  name="name_style"
                  checked={form.name_style === "full_initials"}
                  onChange={() => update("name_style", "full_initials")}
                />
                <span>{nameOptions.full_initials || "—"}</span>
              </label>
              {showPrivacyOption && (
                <label className="flex items-start gap-2 text-sm text-gray-800">
                  <input
                    type="radio"
                    className="mt-0.5 accent-emerald-600"
                    name="name_style"
                    checked={form.name_style === "privacy"}
                    onChange={() => update("name_style", "privacy")}
                  />
                  <span>{nameOptions.privacy || "—"}</span>
                </label>
              )}
              <label className="flex items-start gap-2 text-sm text-gray-800">
                <input
                  type="radio"
                  className="mt-0.5 accent-emerald-600"
                  name="name_style"
                  checked={form.name_style === "custom"}
                  onChange={() => update("name_style", "custom")}
                />
                <span>Custom (may need admin approval)</span>
              </label>
              {form.name_style === "custom" && (
                <label className="block pl-5">
                  <span className={labelClass}>Custom public name</span>
                  <input
                    className={inputClass}
                    required
                    value={form.display_name_custom}
                    onChange={(e) => update("display_name_custom", e.target.value)}
                    placeholder="Name students should see"
                  />
                  {customMatchesLegal && (
                    <span className="mt-1 block text-xs text-red-600">
                      Custom name cannot match your full legal name.
                    </span>
                  )}
                </label>
              )}
            </fieldset>
          </motion.section>

          <motion.section
            className={sectionClass}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 0.05 }}
          >
            <SectionTitle hint="Up to 80 characters. Lead with your specialty.">
              Profile snapshot
            </SectionTitle>
            <label className="block">
              <span className={labelClass}>Headline</span>
              <input
                className={inputClass}
                value={form.headline || ""}
                onChange={(e) => update("headline", e.target.value)}
                maxLength={80}
                placeholder="e.g. Prenatal yoga teacher · Live online classes"
              />
              <span className="mt-1 block text-xs text-gray-500">
                {(form.headline || "").length}/80
              </span>
            </label>
            <div>
              <p className="text-xs text-gray-500 mb-2">Examples — click to use:</p>
              <div className="flex flex-wrap gap-2">
                {HEADLINE_EXAMPLES.map((example) => (
                  <button
                    key={example}
                    type="button"
                    onClick={() => update("headline", example.slice(0, 80))}
                    className="rounded-full border border-emerald-200 bg-emerald-50/80 px-3 py-1.5 text-left text-xs font-medium text-emerald-900 hover:bg-emerald-100 hover:border-emerald-300 transition"
                  >
                    {example}
                  </button>
                ))}
              </div>
            </div>
          </motion.section>

          <motion.section
            className={sectionClass}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 0.1 }}
          >
            <SectionTitle hint="Helps students find teachers in their region and language.">
              Where you teach from
            </SectionTitle>
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block">
                <span className={labelClass}>Country</span>
                <input
                  className={inputClass}
                  value={form.country}
                  onChange={(e) => update("country", e.target.value)}
                />
              </label>
              <label className="block">
                <span className={labelClass}>City</span>
                <input
                  className={inputClass}
                  value={form.city}
                  onChange={(e) => update("city", e.target.value)}
                />
              </label>
            </div>
            <label className="block">
              <span className={labelClass}>Timezone</span>
              <select
                className={inputClass}
                value={form.timezone}
                onChange={(e) => update("timezone", e.target.value)}
                required
              >
                {TIMEZONES.map((tz) => (
                  <option key={tz.value} value={tz.value}>
                    {tz.label}
                  </option>
                ))}
              </select>
            </label>
          </motion.section>

          <motion.section
            id="languages"
            className={sectionClass}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 0.12 }}
          >
            <SectionTitle hint="Select every language you can teach classes in. At least one is required.">
              Languages you teach in
            </SectionTitle>
            <div className="flex flex-wrap gap-2" role="group" aria-label="Teaching languages">
              {LANGUAGE_OPTIONS.map((lang) => {
                const active = form.languages.includes(lang);
                return (
                  <button
                    key={lang}
                    type="button"
                    onClick={() => toggleLanguage(lang)}
                    aria-pressed={active}
                    className={`rounded-xl border px-3 py-1.5 text-sm font-medium transition ${
                      active
                        ? "bg-emerald-600 border-emerald-600 text-white shadow-sm"
                        : "bg-white border-emerald-200 text-emerald-800 hover:bg-emerald-50"
                    }`}
                  >
                    {lang}
                  </button>
                );
              })}
            </div>
            <label className="block">
              <span className={labelClass}>Other language(s)</span>
              <input
                className={inputClass}
                value={form.otherLanguage}
                onChange={(e) => update("otherLanguage", e.target.value)}
                placeholder="e.g. Nepali, Thai (comma-separated)"
              />
            </label>
            {form.languages.length > 0 || form.otherLanguage.trim() ? (
              <p className="text-xs text-emerald-800">
                Selected: {collectLanguages().join(", ")}
              </p>
            ) : (
              <p className="text-xs text-gray-500">Tap languages above or type others.</p>
            )}
          </motion.section>

          <motion.section
            className={sectionClass}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 0.15 }}
          >
            <SectionTitle hint="Optional links and private contact.">Presence</SectionTitle>
            <label className="block">
              <span className={labelClass}>YouTube URL</span>
              <input
                className={inputClass}
                value={form.youtube}
                onChange={(e) => update("youtube", e.target.value)}
                placeholder="https://"
              />
            </label>
            <label className="block">
              <span className={labelClass}>Phone (private, optional)</span>
              <input
                className={inputClass}
                value={form.phone}
                onChange={(e) => update("phone", e.target.value)}
              />
            </label>
          </motion.section>

          <div className="flex flex-col-reverse gap-3 border-t border-emerald-100 pt-4 sm:flex-row sm:items-center sm:justify-between">
            <Link to="/instructor" className="text-center text-sm text-emerald-800 hover:underline sm:text-left">
              Save & exit
            </Link>
            <button
              type="submit"
              disabled={saving || customMatchesLegal}
              className="w-full rounded-xl bg-emerald-700 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-emerald-800 disabled:opacity-60 sm:w-auto"
            >
              {saving ? "Saving…" : "Save & continue"}
            </button>
          </div>
        </form>

        <aside className="lg:sticky lg:top-6">
          <motion.div
            key={`${publicPreview}-${form.headline}-${locationLabel}`}
            initial={{ opacity: 0.6 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-700 to-emerald-900 p-5 text-white shadow-md"
          >
            <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-emerald-100/90">
              Student preview
            </p>
            <p className="mt-3 text-lg font-semibold leading-snug">
              {publicPreview || "Your public name"}
            </p>
            <p className="mt-1 text-sm text-emerald-50/90">
              {form.headline?.trim() || "Your headline will appear here"}
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              {locationLabel ? (
                <span className="rounded-full bg-white/15 px-2.5 py-1 text-xs">{locationLabel}</span>
              ) : (
                <span className="rounded-full bg-white/10 px-2.5 py-1 text-xs text-emerald-100/80">
                  City, country
                </span>
              )}
              {form.timezone ? (
                <span className="rounded-full bg-white/15 px-2.5 py-1 text-xs">{form.timezone}</span>
              ) : null}
              {collectLanguages().slice(0, 4).map((lang) => (
                <span key={lang} className="rounded-full bg-white/15 px-2.5 py-1 text-xs">
                  {lang}
                </span>
              ))}
            </div>
            <p className="mt-4 text-xs leading-relaxed text-emerald-100/75">
              This is how your profile starts to feel for students — refine it as you go.
            </p>
          </motion.div>
        </aside>
      </div>
    </OnboardingStepFrame>
  );
}
